/**
 * AddressAutocomplete.jsx — Google Places address autocomplete with RFOS-themed UI.
 *
 * Uses AutocompleteService (data-only) + our own styled input & dropdown.
 * Supports variant="mui" (MUI TextField) and variant="plain" (native input).
 * Falls back to plain text input if Google Maps isn't available.
 *
 * Dropdown renders via portal (fixed position) so it escapes overflow containers.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { TextField } from '@mui/material';

// ── Config ──
const SB_LOCATION = { lat: 34.42, lng: -119.70 };
const DEBOUNCE_MS = 250;

function waitForGoogle(cb, attempts = 0) {
  if (window.google?.maps?.places?.AutocompleteService) { cb(); return; }
  if (attempts > 80) return;
  setTimeout(() => waitForGoogle(cb, attempts + 1), 100);
}

// ── Dropdown styles ──
const DROP = {
  list: {
    position: 'fixed', zIndex: 99999,
    margin: 0, padding: 0, listStyle: 'none',
    background: '#FFFFFF', border: '1px solid #DFE1E5', borderRadius: 6,
    boxShadow: '0 4px 16px rgba(0,0,0,.12)', maxHeight: 240, overflowY: 'auto',
  },
  item: {
    padding: '10px 14px', fontSize: 13, color: '#1A1A2E', cursor: 'pointer',
    borderBottom: '1px solid #F4F5F7', transition: 'background .1s',
  },
  itemActive: { background: '#F0F1F4' },
  main: { fontWeight: 600 },
  secondary: { color: '#667085', fontSize: 12, marginLeft: 6 },
  powered: {
    padding: '4px 12px 6px', fontSize: 10, color: '#9CA3AF',
    textAlign: 'right', borderTop: '1px solid #F4F5F7',
  },
  typeTag: {
    fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
    marginLeft: 8, display: 'inline-block', verticalAlign: 'middle',
  },
  tagAddress: { background: '#E8F0FE', color: '#1967D2' },
  tagPlace: { background: '#FEF3E2', color: '#B06000' },
};

export default function AddressAutocomplete({
  value = '',
  onChange,
  variant = 'plain',
  label,
  placeholder,
  className,
  size = 'small',
  fullWidth = true,
}) {
  const [inputValue, setInputValue] = useState(value ?? '');
  const [predictions, setPredictions] = useState([]);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [open, setOpen] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });

  const serviceRef = useRef(null);
  const sessionRef = useRef(null);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Sync external value
  useEffect(() => { setInputValue(value ?? ''); }, [value]);

  // Init Google services
  useEffect(() => {
    waitForGoogle(() => {
      serviceRef.current = new window.google.maps.places.AutocompleteService();
      sessionRef.current = new window.google.maps.places.AutocompleteSessionToken();
      setGoogleReady(true);
    });
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Recalculate dropdown position when open
  const updateDropPos = useCallback(() => {
    const el = inputRef.current || wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDropPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, []);

  useEffect(() => {
    if (!open) return;
    updateDropPos();
    // Reposition on scroll/resize
    const scrollParent = wrapRef.current?.closest('[class*="overflow"]') || window;
    const onScroll = () => updateDropPos();
    scrollParent.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      scrollParent.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, updateDropPos]);

  // ── Fetch predictions (addresses + places) ──
  const fetchPredictions = useCallback((input) => {
    if (!serviceRef.current || input.length < 3) { setPredictions([]); return; }
    const locationBias = new window.google.maps.Circle({
      center: new window.google.maps.LatLng(SB_LOCATION.lat, SB_LOCATION.lng),
      radius: 50000,
    });
    const token = sessionRef.current;
    // Fire both geocode (addresses) and establishment (places) queries in parallel
    let pending = 2;
    let combined = [];
    const seen = new Set();
    const merge = (results) => {
      if (results) {
        for (const r of results) {
          if (!seen.has(r.place_id)) { seen.add(r.place_id); combined.push(r); }
        }
      }
      if (--pending === 0) {
        if (combined.length) { setPredictions(combined.slice(0, 7)); setOpen(true); }
        else { setPredictions([]); }
      }
    };
    serviceRef.current.getPlacePredictions(
      { input, sessionToken: token, locationBias, types: ['geocode'] },
      (results, status) => merge(status === 'OK' ? results : null),
    );
    serviceRef.current.getPlacePredictions(
      { input, sessionToken: token, locationBias, types: ['establishment'] },
      (results, status) => merge(status === 'OK' ? results : null),
    );
  }, []);

  // ── Select a prediction ──
  const selectPrediction = useCallback((prediction) => {
    const desc = prediction.description || '';
    setInputValue(desc);
    setPredictions([]);
    setOpen(false);
    setActiveIdx(-1);
    sessionRef.current = new window.google.maps.places.AutocompleteSessionToken();
    onChange?.(desc);
  }, [onChange]);

  // ── Input handlers ──
  const handleChange = (text) => {
    setInputValue(text);
    onChange?.(text);
    clearTimeout(debounceRef.current);
    if (googleReady && text.length >= 3) {
      debounceRef.current = setTimeout(() => fetchPredictions(text), DEBOUNCE_MS);
    } else {
      setPredictions([]);
      setOpen(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!open || predictions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % predictions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + predictions.length) % predictions.length);
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      selectPrediction(predictions[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleBlur = () => {
    setTimeout(() => setOpen(false), 200);
  };

  const handleFocus = () => {
    if (predictions.length) { updateDropPos(); setOpen(true); }
  };

  // ── Dropdown (portalled to body) ──
  const dropdown = open && predictions.length > 0 && createPortal(
    <ul style={{ ...DROP.list, top: dropPos.top, left: dropPos.left, width: dropPos.width }}>
      {predictions.map((p, i) => {
        const main = p.structured_formatting?.main_text || p.description;
        const secondary = p.structured_formatting?.secondary_text || '';
        const isPlace = p.types?.includes('establishment');
        return (
          <li
            key={p.place_id}
            style={{ ...DROP.item, ...(i === activeIdx ? DROP.itemActive : {}) }}
            onMouseEnter={() => setActiveIdx(i)}
            onMouseDown={(e) => { e.preventDefault(); selectPrediction(p); }}
          >
            <span style={DROP.main}>{main}</span>
            {secondary && <span style={DROP.secondary}>{secondary}</span>}
            <span style={{ ...DROP.typeTag, ...(isPlace ? DROP.tagPlace : DROP.tagAddress) }}>
              {isPlace ? 'Place' : 'Address'}
            </span>
          </li>
        );
      })}
      <li style={DROP.powered}>Powered by Google</li>
    </ul>,
    document.body,
  );

  // ── MUI variant ──
  if (variant === 'mui') {
    return (
      <div ref={wrapRef} style={{ position: 'relative', width: fullWidth ? '100%' : undefined }}>
        <TextField
          inputRef={inputRef}
          fullWidth={fullWidth}
          size={size}
          label={label}
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoComplete="off"
        />
        {dropdown}
      </div>
    );
  }

  // ── Plain <input> variant ──
  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />
      {dropdown}
    </div>
  );
}
